/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ObservableComponentBase from "./ObservableComponentBase";

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Implementation of [[ObservableComponentBase]] for observing a double value. */
@cc._decorator.ccclass
export default class ObservableDoubleComponent extends ObservableComponentBase<number>
{
    /** The double value to observe. */
    protected value: number = 0.0;

    /** The specific value to begin at. */
    @property({ type: cc.Float, visible: true })
    private _startValue: number = 0;
    /** Accessor for the start value. */
    public get StartValue(): number { return this._startValue; }

    /** A specified minimum value. */
    @property({ type: cc.Float, visible: true })
    private _minValue: number = 0;
    /** Accessor for the minimum allowed value. */
    public get MinValue(): number { return this._minValue; }
    public set MinValue(value: number) { this._minValue = value; }

    /** A specified maximum value. */
    @property({ type: cc.Float, visible: true })
    public _maxValue: number = 0;
    /** Accessor for the maximum allowed value. */
    public get MaxValue(): number { return this._maxValue; }
    public set MaxValue(value: number) { this._maxValue = value; }

    /** A collection of allowed values, an empty collections means all values are valid. */
    @property({ type: [cc.Float], visible: true})
    protected validValues: number[] = [];

    /** The difference between the current value and the previous value. */
    public get Difference(): number { return this.value - this.oldValue; }

    /** Function to override, if number value, for value. */
    protected numberInputMod: (value: number) => number = null;
    /** Public getter for number input modification function. */
    public get NumberDisplayMod(): (value: number) => number { return this.numberInputMod; }
    /** Public setter for number input  modification function. */
    public set NumberDisplayMod(fnValue: (value: number) => number)
    {
        this.numberInputMod = fnValue;
        // Force displays to update.
        this.broadcastUpdate();
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        if (this.value === 0.0)
        {
            this.SetValue(this.StartValue);
        }
    }

    /**
     * Adds a new valid value to the validValues collection, if it does not already exist.
     * Updates the minimum and maximum values accordingly. If the current value is no longer
     * valid, the current value will be set to the minimum value.
     * @param value The valid value to add.
     */
    public AddValidValue(value: number): ObservableDoubleComponent
    {
        const length: number = this.validValues.length;
        super.AddValidValue(value);
        if (this.validValues.length === length) { return; }

        this.validValues = this.validValues.sort((a, b) => a - b);

        this._minValue = this.validValues[0];
        this._maxValue = this.validValues[this.validValues.length - 1];

        return this;
    }

    // #region Validity Checks

    /**
     * Checks the validity of the current value.
     * @returns If the current value is valid and within the bounds of the minimum and maximum values.
     */
    public IsValid(): boolean
    {
        if (!super.IsValid()) { return false; }
        if (this.outOfRange()) { return false; }

        return true;
    }

    /**
     * Helper for determining if the current value is within the minimum and maximum values.
     * @returns If the current value is within the acceptable range.
     * @hidden
     */
    private outOfRange(): boolean
    {
        return this.value > this._maxValue || this.value < this._minValue;
    }

    // #endregion
}
